import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  BadgeHelp,
  Copy,
  KeyRound,
  LogOut,
  Mail,
  PencilLine,
  QrCode,
  RefreshCw,
  ShieldCheck,
  UserRound,
  X,
} from 'lucide-react';
import type { AuthFormMode, AuthLoginMethod } from '../../shared/types';
import { PasswordField } from './PasswordField';

export interface UserCenterSession {
  status: 'guest' | 'authenticated';
  displayName: string;
  email: string | null;
  displayId: string | null;
  avatarText: string;
  hasAlert: boolean;
  licenseLabel: string;
  remainingLabel: string;
  cardLimitLabel: string;
  expiresAt: string | null;
  permanent: boolean;
  deviceId: string;
  fingerprintSummary: string;
  fingerprintHash: string;
  inviteCode: string;
  invitedCount: number;
  inviteProgressLabel: string;
  bootstrapNotice: string | null;
  secondaryPasswordEnabled: boolean;
  secondaryPasswordTrusted: boolean;
  secondaryPasswordTrustedLabel: string;
  secondaryPasswordUpdatedAt: string | null;
  secondaryPasswordLegacyCloudBackupDetected: boolean;
  scanBound: boolean;
  needsEmailBinding: boolean;
}

export interface UserCenterAuthForm {
  identifier: string;
  email: string;
  password: string;
  confirmPassword: string;
  verificationCode: string;
  inviteCode: string;
  rememberMe: boolean;
  loginMethod: AuthLoginMethod;
}

interface UserCenterDrawerProps {
  open: boolean;
  session: UserCenterSession;
  avatarImageSrc: string | null;
  brandIconSrc: string;
  brandFrameEnabled: boolean;
  authMode: AuthFormMode;
  authForm: UserCenterAuthForm;
  updateBadgeVisible: boolean;
  updateActionBusy: boolean;
  authBusyAction: 'login' | 'register' | 'forgot' | 'logout' | null;
  otpBusy: boolean;
  otpCooldownSeconds: number;
  securityActionBusy: boolean;
  onOpenChange: (open: boolean) => void;
  onAuthModeChange: (mode: AuthFormMode) => void;
  onAuthFormChange: (patch: Partial<UserCenterAuthForm>) => void;
  onSubmitAuth: () => void;
  onSendOtp: () => void;
  onLogout: () => void;
  onEditAvatar: () => void;
  onCopyDeviceCode: () => void;
  onCopyFingerprint: () => void;
  onOpenLicenseCenter: () => void;
  onCheckForUpdates: () => void;
  onOpenAbout: () => void;
  onOpenHelp: () => void;
  onRestartApp: () => void;
  onPreviewLoginScreen: () => void;
  onOpenSecondaryPasswordDialog: () => void;
  onOpenSecondaryPasswordEmailResetDialog: () => void;
  onVerifyCurrentDevice: () => void;
  onBindScanLogin: () => void;
  showPreviewLoginScreen: boolean;
}

const formatCountdown = (
  targetAt: string | null,
  nowMs: number,
  options?: {
    permanent?: boolean;
    emptyLabel?: string;
  },
): string => {
  if (options?.permanent) {
    return '永久有效';
  }

  if (!targetAt) {
    return options?.emptyLabel ?? '未记录';
  }

  const targetMs = new Date(targetAt).getTime();
  if (Number.isNaN(targetMs)) {
    return '未记录';
  }

  const diff = targetMs - nowMs;
  if (diff <= 0) {
    return '已到期';
  }

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}天 ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const getAuthActionLabel = (mode: AuthFormMode): string => {
  switch (mode) {
    case 'register':
      return '注册并进入用户中心';
    case 'forgot':
      return '重置密码';
    default:
      return '登录';
  }
};

export function UserCenterDrawer({
  open,
  session,
  avatarImageSrc,
  brandIconSrc,
  brandFrameEnabled,
  authMode,
  authForm,
  updateBadgeVisible,
  updateActionBusy,
  authBusyAction,
  otpBusy,
  otpCooldownSeconds,
  securityActionBusy,
  onOpenChange,
  onAuthModeChange,
  onAuthFormChange,
  onSubmitAuth,
  onSendOtp,
  onLogout,
  onEditAvatar,
  onCopyDeviceCode,
  onCopyFingerprint,
  onOpenLicenseCenter,
  onCheckForUpdates,
  onOpenAbout,
  onOpenHelp,
  onRestartApp,
  onPreviewLoginScreen,
  onOpenSecondaryPasswordDialog,
  onOpenSecondaryPasswordEmailResetDialog,
  onVerifyCurrentDevice,
  onBindScanLogin,
  showPreviewLoginScreen,
}: UserCenterDrawerProps) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!open || session.status !== 'authenticated') {
      return;
    }

    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [open, session.status]);

  const countdownText = useMemo(
    () =>
      formatCountdown(session.expiresAt, nowMs, {
        permanent: session.permanent,
        emptyLabel: session.remainingLabel || '未授权',
      }),
    [nowMs, session.expiresAt, session.permanent, session.remainingLabel],
  );

  const authActionLabel = getAuthActionLabel(authMode);
  const showOtpBusyIndicator = otpBusy && otpCooldownSeconds <= 0;
  const sendOtpLabel =
    otpCooldownSeconds > 0 ? `${otpCooldownSeconds} 秒后重试` : otpBusy ? '发送中...' : '发送验证码';
  const ambientStyle = useMemo(
    () =>
      ({
        ['--brand-icon-mask' as string]: `url("${brandIconSrc}")`,
      }) as CSSProperties,
    [brandIconSrc],
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <div
          className={`user-drawer-ambient${brandFrameEnabled ? ' is-framed' : ' is-borderless'}`}
          style={ambientStyle}
          aria-hidden="true"
        >
          <div className="user-drawer-ambient__halo" />
          <div className="user-drawer-ambient__stage">
            <span className="user-drawer-ambient__ripple user-drawer-ambient__ripple--outer" />
            <span className="user-drawer-ambient__ripple user-drawer-ambient__ripple--inner" />
            <span
              className={`user-drawer-ambient__mark${brandFrameEnabled ? '' : ' is-borderless'}`}
            >
              <span className="user-drawer-ambient__icon" />
            </span>
          </div>
        </div>
        <Dialog.Content className="user-drawer">
          <header className="user-drawer__header">
            <div>
              <Dialog.Title className="user-drawer__title">用户中心</Dialog.Title>
              <Dialog.Description className="sr-only">
                查看账号信息、授权状态和常用操作。
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="shell-dialog-close shell-dialog-close--plain"
                aria-label="关闭用户中心"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </header>

          <div className="user-drawer__body">
            <section className="user-drawer-profile">
              <button
                type="button"
                className={`user-drawer-profile__avatar user-drawer-profile__avatar--button${avatarImageSrc ? ' has-image' : ''}`}
                onClick={onEditAvatar}
                title="修改头像"
              >
                {avatarImageSrc ? (
                  <img className="user-drawer-profile__avatar-image" src={avatarImageSrc} alt="" />
                ) : (
                  session.avatarText
                )}
              </button>
              <div className="user-drawer-profile__main">
                <strong>{session.status === 'authenticated' ? session.displayName : '未登录'}</strong>
                <span>
                  {session.status === 'authenticated'
                    ? [session.displayId ? `ID ${session.displayId}` : null, session.email]
                        .filter(Boolean)
                        .join(' · ') || '扫码账号待绑定邮箱'
                    : '登录后可查看邮箱与设备归属'}
                </span>
                <div className="user-drawer-inline__actions">
                  {session.status === 'authenticated' && !session.scanBound ? (
                    <button
                      type="button"
                      className="user-drawer-profile__edit-button"
                      onClick={onBindScanLogin}
                      title="绑定扫码登录后可更便捷登录"
                    >
                      <QrCode size={14} />
                      绑定扫码登录
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="user-drawer-profile__edit-button"
                    onClick={onEditAvatar}
                  >
                    <PencilLine size={14} />
                    修改头像
                  </button>
                </div>
              </div>
              {session.hasAlert ? <span className="user-drawer-profile__badge" /> : null}
            </section>

            {session.status === 'authenticated' ? (
              <>
                <section className="user-drawer-section">
                  <div className="user-drawer-section__header">
                    <span>当前授权</span>
                    <span className="badge badge--role-current">{session.licenseLabel}</span>
                  </div>
                  <div className="user-drawer-metric-grid">
                    <div className="about-metric-card">
                      <span className="about-metric-card__label">到期倒计时</span>
                      <strong className="about-metric-card__value">{countdownText}</strong>
                    </div>
                    <div className="about-metric-card">
                      <span className="about-metric-card__label">剩余展示</span>
                      <strong className="about-metric-card__value">{session.remainingLabel}</strong>
                    </div>
                    <div className="about-metric-card">
                      <span className="about-metric-card__label">可用卡片数</span>
                      <strong className="about-metric-card__value">{session.cardLimitLabel}</strong>
                    </div>
                    <button
                      type="button"
                      className="about-metric-card copy-metric-card"
                      onClick={onCopyDeviceCode}
                      title="点击复制完整设备编号"
                    >
                      <span className="about-metric-card__label">设备编号</span>
                      <strong className="about-metric-card__value">{session.deviceId}</strong>
                    </button>
                    <button
                      type="button"
                      className="about-metric-card copy-metric-card"
                      onClick={onCopyFingerprint}
                      title="点击复制完整机器指纹"
                    >
                      <span className="about-metric-card__label">机器指纹</span>
                      <strong className="about-metric-card__value">
                        {session.fingerprintSummary}
                      </strong>
                    </button>
                  </div>
                  <div className="user-drawer-inline__actions">
                    <button
                      type="button"
                      className="sidebar-button sidebar-button--secondary"
                      onClick={onLogout}
                    >
                      {authBusyAction === 'logout' ? (
                        <RefreshCw size={14} className="spin" />
                      ) : (
                        <LogOut size={14} />
                      )}
                      退出登录
                    </button>
                  </div>
                </section>

                <section className="user-drawer-section">
                  <div className="user-drawer-section__header">
                    <span>邀请进度</span>
                    {updateBadgeVisible ? <span className="badge badge--recommended">有更新</span> : null}
                  </div>
                  <div className="user-drawer-inline">
                    <div className="user-drawer-inline__main">
                      <strong>{session.inviteCode}</strong>
                      <span>
                        已邀请 {session.invitedCount} 人 / 当前进度 {session.inviteProgressLabel}
                      </span>
                    </div>
                    <div className="user-drawer-inline__actions">
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary"
                        onClick={onCopyDeviceCode}
                      >
                        <Copy size={14} />
                        复制设备码
                      </button>
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary"
                        onClick={onCopyFingerprint}
                      >
                        <Copy size={14} />
                        复制机器指纹
                      </button>
                    </div>
                  </div>
                </section>

                <section className="user-drawer-section">
                  <div className="user-drawer-section__header">
                    <span>账号安全</span>
                    <span
                      className={`badge ${
                        session.secondaryPasswordEnabled
                          ? session.secondaryPasswordTrusted
                            ? 'badge--recommended'
                            : 'badge--warning'
                          : 'badge--manual'
                      }`}
                    >
                      {session.secondaryPasswordEnabled
                        ? session.secondaryPasswordTrusted
                          ? '已启用'
                          : '待验证'
                        : '未启用'}
                    </span>
                  </div>
                  <div className="user-drawer-inline">
                    <div className="user-drawer-inline__main">
                      <strong>
                        {session.secondaryPasswordEnabled ? '二次密码保护已开启' : '尚未启用二次密码'}
                      </strong>
                      {session.needsEmailBinding ? (
                        <span>当前账号来自扫码登录，建议尽快绑定邮箱密码以保护数据安全。</span>
                      ) : session.scanBound ? (
                        <span>当前账号已绑定扫码登录，可在登录中心直接扫码进入。</span>
                      ) : null}
                    </div>
                    <div className="user-drawer-inline__actions">
                      {session.secondaryPasswordEnabled && !session.secondaryPasswordTrusted ? (
                        <button
                          type="button"
                          className="sidebar-button sidebar-button--secondary"
                          onClick={onVerifyCurrentDevice}
                          disabled={securityActionBusy}
                        >
                          {securityActionBusy ? (
                            <RefreshCw size={14} className="spin" />
                          ) : (
                            <ShieldCheck size={14} />
                          )}
                          验证当前设备
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary"
                        onClick={onOpenSecondaryPasswordDialog}
                        disabled={securityActionBusy}
                      >
                        {securityActionBusy ? (
                          <RefreshCw size={14} className="spin" />
                        ) : (
                          <KeyRound size={14} />
                        )}
                        {session.secondaryPasswordEnabled ? '修改二次密码' : '启用二次密码'}
                      </button>
                      {session.secondaryPasswordEnabled && session.email ? (
                        <button
                          type="button"
                          className="sidebar-button sidebar-button--secondary"
                          onClick={onOpenSecondaryPasswordEmailResetDialog}
                          disabled={securityActionBusy}
                        >
                          {securityActionBusy ? (
                            <RefreshCw size={14} className="spin" />
                          ) : (
                            <Mail size={14} />
                          )}
                          邮箱验证码重置
                        </button>
                      ) : null}
                    </div>
                  </div>
                </section>
              </>
            ) : (
              <section className="user-drawer-section">
                <div className="user-drawer-section__header">
                  <span>登录入口</span>
                  <span className="badge badge--manual">本地授权</span>
                </div>
                <div className="user-drawer-guest-card">
                  <div className="user-drawer-guest-card__tabs">
                    <button
                      type="button"
                      className={`about-qr-tab${authMode === 'login' ? ' is-active' : ''}`}
                      onClick={() => onAuthModeChange('login')}
                    >
                      登录
                    </button>
                    <button
                      type="button"
                      className={`about-qr-tab${authMode === 'register' ? ' is-active' : ''}`}
                      onClick={() => onAuthModeChange('register')}
                    >
                      注册
                    </button>
                    <button
                      type="button"
                      className={`about-qr-tab${authMode === 'forgot' ? ' is-active' : ''}`}
                      onClick={() => onAuthModeChange('forgot')}
                    >
                      忘记密码
                    </button>
                  </div>

                  <input
                    className="license-input"
                    placeholder={authMode === 'login' ? '邮箱或 5 位 ID' : '邮箱'}
                    value={authMode === 'login' ? authForm.identifier : authForm.email}
                    onChange={(event) =>
                      onAuthFormChange(
                        authMode === 'login'
                          ? { identifier: event.target.value }
                          : { email: event.target.value },
                      )
                    }
                  />

                  {authMode === 'login' ? (
                    <div className="user-drawer-guest-card__subtabs">
                      <button
                        type="button"
                        className={`about-qr-tab${authForm.loginMethod === 'password' ? ' is-active' : ''}`}
                        tabIndex={-1}
                        onClick={() => onAuthFormChange({ loginMethod: 'password' })}
                      >
                        密码登录
                      </button>
                      <button
                        type="button"
                        className={`about-qr-tab${authForm.loginMethod === 'otp' ? ' is-active' : ''}`}
                        tabIndex={-1}
                        onClick={() => onAuthFormChange({ loginMethod: 'otp' })}
                      >
                        验证码登录
                      </button>
                    </div>
                  ) : null}

                  {authMode === 'login' && authForm.loginMethod === 'password' ? (
                    <PasswordField
                      placeholder="密码"
                      value={authForm.password}
                      autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                      onChange={(value) => onAuthFormChange({ password: value })}
                    />
                  ) : null}

                  {authMode === 'login' && authForm.loginMethod === 'otp' ? (
                    <div className="user-drawer-guest-card__otp-row">
                      <input
                        className="license-input"
                        placeholder="输入邮箱验证码"
                        value={authForm.verificationCode}
                        onChange={(event) => onAuthFormChange({ verificationCode: event.target.value })}
                      />
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary user-drawer-guest-card__otp-button"
                        onClick={onSendOtp}
                        disabled={otpBusy || otpCooldownSeconds > 0}
                      >
                        {showOtpBusyIndicator ? <RefreshCw size={14} className="spin" /> : null}
                        {sendOtpLabel}
                      </button>
                    </div>
                  ) : null}

                  {authMode === 'register' ? (
                    <div className="user-drawer-guest-card__otp-row">
                      <input
                        className="license-input"
                        placeholder="输入邮箱验证码"
                        value={authForm.verificationCode}
                        onChange={(event) => onAuthFormChange({ verificationCode: event.target.value })}
                      />
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary user-drawer-guest-card__otp-button"
                        onClick={onSendOtp}
                        disabled={otpBusy || otpCooldownSeconds > 0}
                      >
                        {showOtpBusyIndicator ? <RefreshCw size={14} className="spin" /> : null}
                        {sendOtpLabel}
                      </button>
                    </div>
                  ) : null}

                  {authMode === 'register' ? (
                    <PasswordField
                      placeholder="密码"
                      value={authForm.password}
                      autoComplete="new-password"
                      onChange={(value) => onAuthFormChange({ password: value })}
                    />
                  ) : null}

                  {authMode === 'register' ? (
                    <PasswordField
                      placeholder="确认密码"
                      value={authForm.confirmPassword}
                      autoComplete="new-password"
                      onChange={(value) => onAuthFormChange({ confirmPassword: value })}
                    />
                  ) : null}

                  {authMode === 'register' ? (
                    <input
                      className="license-input"
                      placeholder="邀请码（可选，注册时自动绑定）"
                      value={authForm.inviteCode}
                      onChange={(event) => onAuthFormChange({ inviteCode: event.target.value })}
                    />
                  ) : null}

                  {authMode === 'forgot' ? (
                    <>
                      <div className="user-drawer-guest-card__otp-row">
                        <input
                          className="license-input"
                          placeholder="输入邮箱验证码"
                          value={authForm.verificationCode}
                          onChange={(event) =>
                            onAuthFormChange({ verificationCode: event.target.value })
                          }
                        />
                        <button
                          type="button"
                          className="sidebar-button sidebar-button--secondary user-drawer-guest-card__otp-button"
                          onClick={onSendOtp}
                          disabled={otpBusy || otpCooldownSeconds > 0}
                        >
                          {showOtpBusyIndicator ? <RefreshCw size={14} className="spin" /> : null}
                          {sendOtpLabel}
                        </button>
                      </div>
                      <PasswordField
                        placeholder="新密码"
                        value={authForm.password}
                        autoComplete="new-password"
                        onChange={(value) => onAuthFormChange({ password: value })}
                      />
                      <PasswordField
                        placeholder="确认新密码"
                        value={authForm.confirmPassword}
                        autoComplete="new-password"
                        onChange={(value) => onAuthFormChange({ confirmPassword: value })}
                      />
                    </>
                  ) : null}

                  {authMode === 'login' && authForm.loginMethod === 'password' ? (
                    <label className="user-drawer-remember">
                      <input
                        type="checkbox"
                        checked={authForm.rememberMe}
                        onChange={(event) => onAuthFormChange({ rememberMe: event.target.checked })}
                      />
                      <span>记住密码</span>
                    </label>
                  ) : null}

                  <button type="button" className="sidebar-button" onClick={onSubmitAuth}>
                    {authBusyAction === 'login' ||
                    authBusyAction === 'register' ||
                    authBusyAction === 'forgot' ? (
                      <RefreshCw size={14} className="spin" />
                    ) : (
                      <UserRound size={14} />
                    )}
                    {authActionLabel}
                  </button>
                </div>
              </section>
            )}

            <section className="user-drawer-actions">
              <button type="button" className="user-drawer-action" onClick={onOpenLicenseCenter}>
                <KeyRound size={16} />
                <span>打开授权中心</span>
              </button>
              {showPreviewLoginScreen ? (
                <button type="button" className="user-drawer-action" onClick={onPreviewLoginScreen}>
                  <UserRound size={16} />
                  <span>预览登录界面</span>
                </button>
              ) : null}
              <button type="button" className="user-drawer-action" onClick={onCheckForUpdates}>
                {updateActionBusy ? (
                  <RefreshCw size={16} className="spin" />
                ) : (
                  <RefreshCw size={16} />
                )}
                <span>检查更新</span>
              </button>
              <button type="button" className="user-drawer-action" onClick={onOpenAbout}>
                <ShieldCheck size={16} />
                <span>关于</span>
              </button>
              <button type="button" className="user-drawer-action" onClick={onOpenHelp}>
                <BadgeHelp size={16} />
                <span>帮助</span>
              </button>
              <button type="button" className="user-drawer-action" onClick={onRestartApp}>
                <RefreshCw size={16} />
                <span>重启应用</span>
              </button>
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
